<script setup>
import { computed } from 'vue'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useBillingStore } from '@/stores/billing'
import { useSessionStore } from '@/stores/session'
import { discountSize } from '@/helpers/billing-format'
import { countryName, taxIdLabel } from '@/helpers/billing-details'

/**
 * Where invoices are sent and how they are paid: the card, the billing details
 * (company, address, emails, tax ID) and the discount code. Each one opens its
 * own slide-in; people without billing permission see the same facts without
 * the buttons.
 */
const emit = defineEmits([
    'edit-payment-method',
    'edit-details',
    'edit-discount',
])

const billingStore = useBillingStore()
const sessionStore = useSessionStore()
const { can } = usePermissions()

const canManage = computed(() => can(PERMISSIONS.BILLING_UPDATE))
const paymentMethod = computed(
    () => billingStore.summary?.payment_method ?? null
)
const discount = computed(() => billingStore.summary?.discount ?? null)
const account = computed(() => sessionStore.data?.account?.data ?? null)

const addressLines = computed(() => {
    const a = account.value
    if (!a) return []

    const cityLine = [a.billing_city, a.billing_state, a.billing_postal_code]
        .filter(Boolean)
        .join(', ')

    return [
        a.billing_company_name,
        a.billing_line1,
        a.billing_line2,
        cityLine,
        a.billing_country ? countryName(a.billing_country) : null,
    ].filter(Boolean)
})

const emails = computed(() => {
    const a = account.value
    if (!a) return []

    return [a.billing_email, ...(a.billing_additional_emails ?? [])].filter(
        Boolean
    )
})

const taxId = computed(() =>
    taxIdLabel(
        account.value?.billing_tax_id_type,
        account.value?.billing_tax_id
    )
)

const discountTerm = computed(() => {
    const d = discount.value
    if (!d) return null

    if (d.duration_type === 'forever') return 'for as long as you stay'
    if (d.months_remaining === null || d.months_remaining === undefined) {
        return null
    }
    if (d.months_remaining === 1) return '1 month left'
    return `${d.months_remaining} months left`
})
</script>

<template>
    <section
        data-testid="payment-details-card"
        aria-labelledby="payment-details-heading"
        class="rounded-xl border border-taupe-200 bg-white p-6 shadow-sm"
    >
        <h2
            id="payment-details-heading"
            class="text-sm font-medium uppercase tracking-wide text-taupe-600"
        >
            Payment &amp; details
        </h2>

        <div
            v-if="billingStore.loading.summary && !billingStore.summary"
            class="mt-4 grid gap-6 md:grid-cols-3"
            data-testid="payment-details-loading"
        >
            <zen-skeleton class="h-16 w-full" />
            <zen-skeleton class="h-16 w-full" />
            <zen-skeleton class="h-16 w-full" />
        </div>

        <dl v-else class="mt-4 grid gap-6 md:grid-cols-3">
            <div data-testid="payment-method-section">
                <dt class="text-sm font-medium text-taupe-900">Card</dt>
                <dd class="mt-1 text-sm text-taupe-600">
                    <template v-if="paymentMethod?.present">
                        <div data-testid="payment-method-description">
                            {{ paymentMethod.description }}
                        </div>
                        <div v-if="paymentMethod.expires_on">
                            Expires
                            <zen-date-time
                                :value="paymentMethod.expires_on"
                                format="date"
                                utc
                            />
                        </div>
                    </template>
                    <template v-else>
                        <span data-testid="payment-method-none"
                            >No card on file.</span
                        >
                    </template>
                </dd>
                <zen-button
                    v-if="canManage"
                    class="mt-3"
                    variant="secondary"
                    size="small"
                    data-testid="edit-payment-method"
                    @click="emit('edit-payment-method')"
                >
                    {{ paymentMethod?.present ? 'Update card' : 'Add card' }}
                </zen-button>
            </div>

            <div data-testid="billing-details-section">
                <dt class="text-sm font-medium text-taupe-900">
                    Billing details
                </dt>
                <dd class="mt-1 text-sm text-taupe-600">
                    <template v-if="addressLines.length || emails.length">
                        <div
                            v-for="line in addressLines"
                            :key="line"
                            data-testid="billing-address-line"
                        >
                            {{ line }}
                        </div>
                        <div
                            v-for="email in emails"
                            :key="email"
                            class="break-all"
                            data-testid="billing-email"
                        >
                            {{ email }}
                        </div>
                        <div v-if="taxId" data-testid="billing-tax-id">
                            {{ taxId }}
                        </div>
                    </template>
                    <span v-else data-testid="billing-details-none"
                        >No billing address yet.</span
                    >
                </dd>
                <zen-button
                    v-if="canManage"
                    class="mt-3"
                    variant="secondary"
                    size="small"
                    data-testid="edit-details"
                    @click="emit('edit-details')"
                >
                    Edit details
                </zen-button>
            </div>

            <div data-testid="discount-section">
                <dt class="text-sm font-medium text-taupe-900">
                    Discount code
                </dt>
                <dd class="mt-1 text-sm text-taupe-600">
                    <template v-if="discount">
                        <div data-testid="discount-code">
                            <span class="font-medium text-taupe-900">{{
                                discount.code
                            }}</span>
                            · {{ discountSize(discount) }}
                        </div>
                        <div v-if="discountTerm" data-testid="discount-term">
                            {{ discountTerm }}
                        </div>
                    </template>
                    <span v-else data-testid="discount-none"
                        >No discount code applied.</span
                    >
                </dd>
                <zen-button
                    v-if="canManage"
                    class="mt-3"
                    variant="secondary"
                    size="small"
                    data-testid="edit-discount"
                    @click="emit('edit-discount')"
                >
                    {{ discount ? 'Replace code' : 'Add code' }}
                </zen-button>
            </div>
        </dl>
    </section>
</template>
